import React, { useEffect, useState } from "react";
import { WEBHOOK_NAMES } from "../../../../../data/constants/webhooks";
import logger from "../../../../../lib/logger";
import { Checkbox, Label, Loader, Tooltip } from "@vibe/core";
import { IoSync, IoCheckmark, IoClose } from "react-icons/io5";
import { useWebhooks } from "../../../../../hooks/services";
import { IconButton } from "@vibe/core";
import { GrInstallOption } from "react-icons/gr";
import WebhookOwnershipDropdown from "./components/WebhookOwnershipDropdown";
import useTokenHook from "../../../../../hooks/context/useTokenHook";
import useCheckAccessLevelCurrentBoard from "../../../../../hooks/api/useCheckAccessLevel";
import { reinstallSelectedWebhooks } from "../../../../../utils/requests/webhookManagement";

const PERMISSION_ERROR_HINT =
  "Unable to modify this webhook. The webhook owner or a board admin needs to reinstall it from their account.";

const getFriendlyErrorMessage = (error?: string): string => {
  if (!error) return "Unknown error";
  if (error === "Internal server error") return PERMISSION_ERROR_HINT;
  return error;
};

export interface Webhook {
  id?: string;
  event: string;
  board_id?: number;
  config?: any;
}

type WebhookStatus =
  | "installed"
  | "not_installed"
  | "reinstalling"
  | "success"
  | "error"
  | "loading";

interface WebhookState {
  status: WebhookStatus;
  error?: string;
}

const WebhooksSettings = ({
  boardId,
  authorizedUsers,
  authorizedUsersLoading,
  authorizedUsersError,
}: {
  boardId: number | null;
  authorizedUsers: any[];
  authorizedUsersLoading: boolean;
  authorizedUsersError: boolean;
}) => {
  const { webhooks, uninstalled, isLoading, isValidating, mutate } = useWebhooks(boardId!);
  const [selectedWebhooks, setSelectedWebhooks] = useState<Webhook[]>([]);
  const [isReinstalling, setIsReinstalling] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [webhookStates, setWebhookStates] = useState<Record<string, WebhookState>>(() => {
    // Initialize with loading state
    const initialStates: Record<string, WebhookState> = {};
    Object.keys(WEBHOOK_NAMES).forEach((name) => {
      initialStates[name] = { status: "loading" };
    });
    return initialStates;
  });
  const { token } = useTokenHook();

  const { data: accessLevel, isLoading: accessLevelLoading } = useCheckAccessLevelCurrentBoard();

  useEffect(() => {
    // Don't update state while loading or validating - preserve existing state
    if (isLoading || isValidating) {
      return;
    }

    // Mark that we've loaded data at least once
    if (!hasLoadedOnce) {
      setHasLoadedOnce(true);
    }

    const newStates: Record<string, WebhookState> = {};

    // Set status for installed webhooks
    webhooks.forEach((webhook) => {
      newStates[webhook.event] = { status: "installed" };
    });

    // Set status for uninstalled webhooks
    Object.keys(WEBHOOK_NAMES).forEach((name) => {
      if (!webhooks.some((w) => w.event === name)) {
        newStates[name] = { status: "not_installed" };
      }
    });

    // Only update if the status has actually changed
    const hasChanged = Object.keys(newStates).some(
      (key) => newStates[key].status !== webhookStates[key]?.status,
    );

    if (hasChanged) {
      setWebhookStates(newStates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webhooks, uninstalled, isLoading, isValidating]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedWebhooks([...webhooks, ...uninstalled]);
    } else {
      setSelectedWebhooks([]);
    }
  };

  const handleSelectWebhook = (webhook: Webhook, checked: boolean) => {
    if (checked) {
      setSelectedWebhooks((prev) => [...prev, webhook]);
    } else {
      setSelectedWebhooks((prev) => prev.filter((w) => w.id !== webhook.id));
    }
  };

  const handleReinstallWebhooks = async (webhook?: Webhook) => {
    try {
      setIsReinstalling(true);
      const webhooksToReinstall = webhook
        ? [webhook]
        : selectedWebhooks.length > 0
          ? selectedWebhooks
          : [];

      const reinstallingStates = webhooksToReinstall.reduce(
        (acc, wh) => ({
          ...acc,
          [wh?.event]: { status: "reinstalling" as WebhookStatus },
        }),
        {},
      );
      setWebhookStates((prev) => ({ ...prev, ...reinstallingStates }));

      const results = await reinstallSelectedWebhooks(
        boardId!,
        webhooksToReinstall.map((wh) => ({ id: wh.id, event: wh.event })),
      );

      const resultStates = results.reduce(
        (acc, result) => ({
          ...acc,
          [result.event]: {
            status:
              result.status === "success"
                ? ("success" as WebhookStatus)
                : ("error" as WebhookStatus),
            error: getFriendlyErrorMessage(result.error),
          },
        }),
        {},
      );
      setWebhookStates((prev) => ({ ...prev, ...resultStates }));

      const hasErrors = results.some((r) => r.status === "error");
      setTimeout(
        async () => {
          await mutate();
        },
        hasErrors ? 5000 : 1500,
      );
    } catch (error: any) {
      logger.error("Error reinstalling webhooks", error);

      const friendlyMessage = getFriendlyErrorMessage(error.message);
      const errorStates = (webhook ? [webhook] : selectedWebhooks).reduce(
        (acc, wh) => ({
          ...acc,
          [wh?.event]: { status: "error" as WebhookStatus, error: friendlyMessage },
        }),
        {},
      );
      setWebhookStates((prev) => ({ ...prev, ...errorStates }));

      setTimeout(async () => {
        await mutate();
      }, 5000);
    } finally {
      setSelectedWebhooks([]);
      setIsReinstalling(false);
    }
  };

  if (isLoading || accessLevelLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader size="small" color="primary" />
      </div>
    );
  }

  if (accessLevel && accessLevel !== "edit") {
    return (
      <div className="flex justify-center items-center h-full">
        <p className="text-sm text-center text-balance">
          You do not have permission to edit webhooks on this board. Only users with proper
          permissions and board owners can edit webhooks.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border">
        <div className="flex justify-between items-center py-3 px-2 font-medium border-b border-[#E7E9EF] border-t-0 border-r-0 border-l-0 border-solid">
          <div className="flex gap-2">
            <Checkbox
              checked={selectedWebhooks.length === webhooks.length + uninstalled.length}
              label="Select All"
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
          </div>
          {selectedWebhooks.length > 0 && (
            <div className="flex gap-2 justify-end">
              <IconButton
                kind="secondary"
                size="small"
                onClick={() => handleReinstallWebhooks()}
                loading={isReinstalling}
                disabled={isReinstalling}
                icon={IoSync}
                tooltipContent="Install Selected"
              />
              <Tooltip
                content={
                  !selectedWebhooks.every((webhook) => webhook.id)
                    ? "You can only transfer ownership of installed webhooks"
                    : ""
                }
              >
                <div>
                  <WebhookOwnershipDropdown
                    webhooks={selectedWebhooks.filter((webhook) => webhook.id)}
                    authorizedUsers={authorizedUsers}
                    authorizedUsersLoading={authorizedUsersLoading}
                    authorizedUsersError={authorizedUsersError}
                    boardId={boardId!}
                    token={token!}
                    revalidate={mutate}
                    setWebhookStates={setWebhookStates}
                    setSelectedWebhooks={setSelectedWebhooks}
                    disabled={!selectedWebhooks.every((webhook) => webhook.id)}
                  />
                </div>
              </Tooltip>
            </div>
          )}
        </div>
        <div>
          {Object.entries(WEBHOOK_NAMES).map(([name, description]) => {
            const webhook = webhooks.find((webhook) => webhook?.event === name);
            const state = webhookStates[name];
            const status = state?.status;
            const error = state?.error;

            return (
              <div
                key={name}
                className="grid grid-cols-[15.625rem_1fr_1fr] py-3 px-2 border-[#E7E9EF] border-solid border-b border-l-0 border-r-0 border-t-0"
              >
                <div className="flex gap-2 items-center">
                  <Checkbox
                    checked={selectedWebhooks.some(
                      (w) =>
                        (w.event === webhook?.event && w.id === webhook?.id) ||
                        (w.event === name && !webhook?.id),
                    )}
                    onChange={(e) =>
                      handleSelectWebhook(
                        { event: webhook?.event || name, id: webhook?.id },
                        e.target.checked,
                      )
                    }
                  />
                  <Tooltip content={description}>
                    <div className="overflow-hidden text-sm font-medium whitespace-nowrap text-ellipsis">
                      {description}
                    </div>
                  </Tooltip>
                </div>

                <div className="flex justify-center items-center text-sm text-light-secondary-text-color">
                  {status === "loading" ? (
                    <div className="w-20 h-6 bg-gray-200 rounded animate-pulse" />
                  ) : status === "installed" || status === "success" ? (
                    <Label text="Installed" color="positive" />
                  ) : (
                    <Label text="Not Installed" color="dark" />
                  )}
                </div>

                <div className="flex justify-end items-end">
                  {(() => {
                    if (status === "loading") {
                      return <div className="w-24 h-8 bg-gray-200 rounded animate-pulse" />;
                    }

                    if (status === "reinstalling") {
                      return <IoSync className="text-blue-500 animate-spin" size={24} />;
                    }

                    if (status === "success") {
                      return <IoCheckmark className="text-green-500" size={24} />;
                    }

                    if (status === "error") {
                      return (
                        <div className="flex gap-2 items-center max-w-full cursor-help">
                          <IoClose className="flex-shrink-0 text-red-500" size={20} />
                          <Tooltip content={error}>
                            <span className="overflow-hidden text-xs text-red-500 whitespace-nowrap text-ellipsis max-w-[10rem]">
                              {error}
                            </span>
                          </Tooltip>
                        </div>
                      );
                    }

                    if (status === "installed" || status === "not_installed") {
                      return (
                        <div className="flex gap-2 items-center">
                          <IconButton
                            icon={webhook ? IoSync : GrInstallOption}
                            kind="secondary"
                            size="small"
                            tooltipContent={webhook ? "Reinstall" : "Install"}
                            onClick={() =>
                              handleReinstallWebhooks({
                                event: webhook?.event || name,
                                id: webhook?.id,
                              })
                            }
                            disabled={isReinstalling}
                          />

                          {webhook && (
                            <WebhookOwnershipDropdown
                              webhooks={[webhook]}
                              authorizedUsers={authorizedUsers}
                              authorizedUsersLoading={authorizedUsersLoading}
                              authorizedUsersError={authorizedUsersError}
                              boardId={boardId!}
                              token={token!}
                              revalidate={mutate}
                              setWebhookStates={setWebhookStates}
                            />
                          )}
                        </div>
                      );
                    }

                    return null;
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WebhooksSettings;
